import clsx from 'clsx';
import { KIcon } from './KIcon.js';

export interface FaseStap {
  id: string;
  naam: string;
  /** Bestaande trajectstap-status: completed | active | pending (andere waarden = toekomst). */
  status: string;
  datum?: string | null;
}

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('nl-BE', { timeZone: 'Europe/Brussels', day: 'numeric', month: 'short' }) : null;

/**
 * Faseband (Kordaro v4): enkel bestaande trajectstappen. Segment 8 hoog, gap 4, labels 13.
 * Voltooid = espresso + "afgerond", huidig = merkkleur + "huidige fase" (géén vink),
 * toekomst = rand. Gsm: compacte huidige + volgende fase, volledige band via "Alle fasen".
 */
export function Faseband({ stappen, titel = 'Fasen' }: { stappen: FaseStap[]; titel?: string }) {
  if (stappen.length === 0) return null;
  const huidigIdx = stappen.findIndex((s) => s.status === 'active');
  const huidig = huidigIdx >= 0 ? stappen[huidigIdx] : null;
  const volgende = stappen.slice(huidigIdx + 1).find((s) => s.status !== 'completed') ?? null;

  const lijst = (
    <ol className="grid gap-1" style={{ gridTemplateColumns: `repeat(${stappen.length}, minmax(0, 1fr))` }} aria-label={titel}>
      {stappen.map((s) => {
        const klaar = s.status === 'completed';
        const actief = s.status === 'active';
        return (
          <li key={s.id} className="min-w-0" aria-current={actief ? 'step' : undefined}>
            <span className={clsx('block h-2 rounded-full', klaar ? 'bg-gray-900' : actief ? 'bg-brand' : 'border border-gray-400 bg-white')} aria-hidden="true" />
            <span className={clsx('mt-1 block text-[13px] leading-5 truncate', actief ? 'text-brand font-semibold' : klaar ? 'text-gray-800' : 'text-gray-500')} title={s.naam}>
              {s.naam}
            </span>
            <span className="k-sr-only">{actief ? '— huidige fase' : klaar ? '— afgerond' : '— nog niet gestart'}</span>
            {fmt(s.datum) && <span className="block text-[13px] leading-5 text-gray-500">{fmt(s.datum)}</span>}
          </li>
        );
      })}
    </ol>
  );

  return (
    <section aria-label={titel}>
      <div className="hidden md:block">{lijst}</div>
      <div className="md:hidden">
        <p className="text-base">
          <span className="k-meta block">Huidige fase</span>
          <span className="font-semibold text-gray-900">{huidig ? huidig.naam : 'Geen actieve fase'}</span>
          {huidig && fmt(huidig.datum) && <span className="text-gray-600"> · {fmt(huidig.datum)}</span>}
        </p>
        {volgende && (
          <p className="text-sm text-gray-700 mt-1 flex items-center gap-1">
            <KIcon naam="pijl" size={16} /> Daarna: {volgende.naam}{fmt(volgende.datum) ? ` · ${fmt(volgende.datum)}` : ''}
          </p>
        )}
        <details className="mt-2">
          <summary className="k-button k-button--text !px-0 cursor-pointer">Alle fasen ({stappen.length})</summary>
          <ul className="mt-2">
            {stappen.map((s) => (
              <li key={s.id} className="flex justify-between gap-2 py-2 border-b border-card-border text-sm">
                <span className={clsx(s.status === 'active' && 'font-semibold text-brand')}>{s.naam}</span>
                <span className="k-meta">{s.status === 'completed' ? 'Afgerond' : s.status === 'active' ? 'Huidige fase' : 'Nog niet gestart'}{fmt(s.datum) ? ` · ${fmt(s.datum)}` : ''}</span>
              </li>
            ))}
          </ul>
        </details>
      </div>
    </section>
  );
}
